import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Package, Plus, Trash2, BookOpen, Calendar, Sparkles } from "lucide-react";
import { StatusBadge } from "./Dashboard";

export function BundlesCatalog() {
  const { user, API } = useAuth();
  const [bundles, setBundles] = useState([]);

  useEffect(() => {
    axios.get(`${API}/bundles`, { withCredentials: true }).then((r) => setBundles(r.data));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Combo deals</div>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#0A231C]">Bundles</h1>
        <p className="text-sm text-[#5C5C5C] mt-2">Course + webinar packs — same content, better price.</p>
      </div>
      {bundles.length === 0 ? (
        <div className="card-surface p-10 text-center text-[#5C5C5C]" data-testid="no-bundles">
          No bundles available right now.
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6" data-testid="bundles-grid">
          {bundles.map((b) => (
            <Link key={b.id} to={`/bundles/${b.id}`} className="course-card card-surface block" data-testid={`bundle-card-${b.id}`}>
              <div className="h-40 overflow-hidden rounded-t-[14px] bg-[#0A231C]">
                {b.cover_image ? (
                  <img src={b.cover_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white"><Package size={40} /></div>
                )}
              </div>
              <div className="p-5">
                <div className="overline flex items-center gap-1"><Sparkles size={12} className="text-[#D45D3C]" /> Bundle</div>
                <div className="font-heading font-semibold text-[#0A231C] mt-1">{b.title}</div>
                <div className="mt-1 text-xs text-[#5C5C5C]">
                  {(b.course_ids?.length || 0)} courses · {(b.webinar_ids?.length || 0)} webinars
                </div>
                <div className="mt-3 font-heading font-semibold text-[#0A231C]">₹{b.price}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function BundleDetail() {
  const { id } = useParams();
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () => axios.get(`${API}/bundles/${id}`, { withCredentials: true }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]);

  const purchase = async () => {
    try {
      const r = await axios.post(`${API}/bundles/${id}/purchase`, {}, { withCredentials: true });
      toast.success(`Enrolled in ${r.data.courses_enrolled} courses + ${r.data.webinars_registered} webinars`);
      navigate("/my-courses");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purchase failed");
    }
  };

  if (!data) return <div className="p-10">Loading…</div>;
  const { bundle: b, courses, webinars, original_price, savings } = data;

  return (
    <div className="grid md:grid-cols-12 gap-8" data-testid="bundle-detail">
      <section className="md:col-span-8 space-y-6">
        <div className="overline flex items-center gap-1"><Sparkles size={12} className="text-[#D45D3C]" /> Bundle</div>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#0A231C]">{b.title}</h1>
        <p className="text-[#5C5C5C] leading-relaxed">{b.description}</p>

        {b.cover_image && <img src={b.cover_image} alt="" className="w-full h-64 object-cover rounded-2xl" />}

        <div>
          <div className="overline mb-2 flex items-center gap-1"><BookOpen size={12} /> Included courses ({courses.length})</div>
          <div className="space-y-2">
            {courses.map((c) => (
              <div key={c.id} className="card-surface p-4 flex items-center justify-between" data-testid={`bundle-course-${c.id}`}>
                <div>
                  <div className="font-heading font-medium">{c.title}</div>
                  <div className="text-xs text-[#5C5C5C]">{c.category} · by {c.instructor_name}</div>
                </div>
                <div className="text-sm text-[#5C5C5C] line-through">₹{c.price}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="overline mb-2 flex items-center gap-1"><Calendar size={12} /> Included webinars ({webinars.length})</div>
          <div className="space-y-2">
            {webinars.map((w) => (
              <div key={w.id} className="card-surface p-4 flex items-center justify-between" data-testid={`bundle-webinar-${w.id}`}>
                <div>
                  <div className="font-heading font-medium">{w.title}</div>
                  <div className="text-xs text-[#5C5C5C]">{new Date(w.scheduled_at).toLocaleString()}</div>
                </div>
                <div className="text-sm text-[#5C5C5C] line-through">₹{w.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <aside className="md:col-span-4">
        <div className="card-surface p-6 sticky top-24" data-testid="bundle-purchase-panel">
          <div className="overline">Bundle price</div>
          <div className="mt-1 font-heading text-4xl font-semibold text-[#0A231C]">₹{b.price}</div>
          {original_price > 0 && (
            <div className="mt-1 text-sm text-[#5C5C5C]">
              <span className="line-through">₹{original_price}</span>
              {savings > 0 && <span className="ml-2 text-[#2E7D32] font-medium">Save ₹{savings}</span>}
            </div>
          )}
          {user?.role === "learner" && (
            <button onClick={purchase} className="btn-primary w-full mt-5" data-testid="purchase-bundle-btn">
              Buy bundle
            </button>
          )}
          {user?.role === "admin" && (
            <button onClick={() => navigate("/admin/bundles")} className="btn-outline w-full mt-5 text-sm">
              Manage bundles
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}

export function AdminBundles() {
  const { API } = useAuth();
  const [bundles, setBundles] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [courses, setCourses] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", price: 999,
    cover_image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800",
    course_ids: [], webinar_ids: [],
  });

  const load = () => axios.get(`${API}/bundles`, { withCredentials: true }).then((r) => setBundles(r.data));
  useEffect(() => {
    load();
    axios.get(`${API}/courses?status=approved`, { withCredentials: true }).then((r) => setCourses(r.data));
    axios.get(`${API}/webinars?status=approved`, { withCredentials: true }).then((r) => setWebinars(r.data));
  }, []);

  const toggle = (list, id) => list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const create = async (e) => {
    e.preventDefault();
    if (!form.course_ids.length && !form.webinar_ids.length) return toast.error("Select at least one item");
    try {
      await axios.post(`${API}/admin/bundles`, form, { withCredentials: true });
      toast.success("Bundle created");
      setShowNew(false);
      setForm({ ...form, title: "", description: "", course_ids: [], webinar_ids: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this bundle?")) return;
    await axios.delete(`${API}/admin/bundles/${id}`, { withCredentials: true });
    toast.success("Deactivated");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Combo deals</div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#0A231C]">Bundles</h1>
        </div>
        <button onClick={() => setShowNew(!showNew)} className="btn-primary flex items-center gap-2" data-testid="new-bundle-btn">
          <Plus size={16} /> {showNew ? "Cancel" : "Create bundle"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="card-surface p-6 space-y-4" data-testid="new-bundle-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Title"><input required className="input" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="bundle-title-input" /></Field>
            <Field label="Bundle price (INR)"><input required type="number" min={0} className="input" value={form.price} onChange={(e) => setForm({...form, price: parseFloat(e.target.value||0)})} data-testid="bundle-price-input" /></Field>
          </div>
          <Field label="Description"><textarea rows={2} className="input" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="bundle-desc-input" /></Field>
          <Field label="Cover image URL"><input className="input" value={form.cover_image} onChange={(e) => setForm({...form, cover_image: e.target.value})} /></Field>

          <div>
            <div className="overline mb-2">Select courses ({form.course_ids.length} selected)</div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {courses.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm p-2 rounded hover:bg-[#F1EFE7]">
                  <input type="checkbox" checked={form.course_ids.includes(c.id)}
                         onChange={() => setForm({...form, course_ids: toggle(form.course_ids, c.id)})}
                         data-testid={`bundle-course-check-${c.id}`} />
                  <span className="flex-1">{c.title} — <span className="text-[#5C5C5C]">₹{c.price}</span></span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="overline mb-2">Select webinars ({form.webinar_ids.length} selected)</div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {webinars.map((w) => (
                <label key={w.id} className="flex items-center gap-2 text-sm p-2 rounded hover:bg-[#F1EFE7]">
                  <input type="checkbox" checked={form.webinar_ids.includes(w.id)}
                         onChange={() => setForm({...form, webinar_ids: toggle(form.webinar_ids, w.id)})}
                         data-testid={`bundle-webinar-check-${w.id}`} />
                  <span className="flex-1">{w.title} — <span className="text-[#5C5C5C]">₹{w.price}</span></span>
                </label>
              ))}
            </div>
          </div>

          <button type="submit" className="btn-primary" data-testid="create-bundle-submit">Create bundle</button>
        </form>
      )}

      <div className="card-surface overflow-x-auto">
        <table className="lms-table w-full">
          <thead><tr><th>Title</th><th>Includes</th><th>Price</th><th>Status</th><th className="text-right pr-4">Action</th></tr></thead>
          <tbody>
            {bundles.map((b) => (
              <tr key={b.id} data-testid={`admin-bundle-row-${b.id}`}>
                <td className="font-heading font-medium">{b.title}</td>
                <td className="text-sm text-[#5C5C5C]">{b.course_ids?.length || 0} courses · {b.webinar_ids?.length || 0} webinars</td>
                <td>₹{b.price}</td>
                <td><StatusBadge s={b.status === "active" ? "approved" : "rejected"} /></td>
                <td className="text-right pr-4">
                  <Link to={`/bundles/${b.id}`} className="text-sm text-[#0A231C] hover:underline mr-3">View</Link>
                  {b.status === "active" && (
                    <button onClick={() => deactivate(b.id)} className="text-sm text-[#C2482A]" data-testid={`deactivate-bundle-${b.id}`}>Deactivate</button>
                  )}
                </td>
              </tr>
            ))}
            {bundles.length === 0 && <tr><td colSpan={5} className="text-center text-sm text-[#5C5C5C] py-8">No bundles yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <style>{`.input{width:100%;padding:0.6rem 0.85rem;border:1px solid #E5E3D8;border-radius:8px;background:white;outline:none;font-family:inherit;font-size:0.9rem}.input:focus{border-color:#0A231C}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline mb-1">{label}</div>
      {children}
    </label>
  );
}
